import { ApiError } from "../../api/client";
import type {
  Modality,
  PlaygroundCapability,
  PlaygroundRouting,
  PlaygroundHistoryItem,
  PlaygroundImageInput,
  PlaygroundImageRoute,
  PlaygroundVideoInput,
  PlaygroundVideoRoute,
} from "../../api/types";

// Page-wide limits shared by the form, uploads and polling.
// 表单、上传和轮询共用的页面级限制。
export const PLAYGROUND_UPLOAD_HARD_LIMIT = 9;
export const PLAYGROUND_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;
export const PLAYGROUND_UPLOAD_TYPES = ["image/png", "image/jpeg", "image/webp"];
export const PLAYGROUND_POLL_INTERVAL_MS = 3000;
export const PLAYGROUND_NOT_CONFIGURED_CODE = "PLAYGROUND_NOT_CONFIGURED";

const IMAGE_MODES = ["text_to_image", "image_to_image"];
const VIDEO_MODES = ["text_to_video", "image_to_video", "reference_to_video"];
const PREFERRED_ASPECT_RATIOS = ["16:9"];
// image_to_video only has a first and a last frame slot.
// 图生视频只有首帧和尾帧两个图片位。
const IMAGE_TO_VIDEO_MAX_FRAMES = 2;

// PlaygroundRoute is one normalized capability route; empty enums accept any value like the backend matcher.
// 规范化后的单条能力路由；空枚举与后端匹配规则一致，表示不限制。
export interface PlaygroundRoute {
  mode: string;
  aspectRatios: string[];
  resolutions: string[];
  durations: number[];
  minImages: number;
  maxImages: number | null;
}

export interface PlaygroundModel {
  key: string;
  modality: Modality;
  routes: PlaygroundRoute[];
}

export interface PlaygroundSelection {
  model: string;
  aspectRatio: string;
  resolution: string;
  durationSeconds: number;
}

export interface ModeResolution {
  allowed: boolean;
  mode: string | null;
  // routes are the chosen mode's routes that accept the current image count.
  // routes 为当前模式下能接受当前图片数量的路由。
  routes: PlaygroundRoute[];
  hint: string;
}

export interface PlaygroundForm {
  modality: Modality;
  model: PlaygroundModel | null;
  mode: ModeResolution;
  uploadLimit: number;
  aspectRatios: string[];
  resolutions: string[];
  durations: number[];
  selection: PlaygroundSelection;
  blockedReason: string;
}

export type PlaygroundPayload =
  | { modality: "image"; input: PlaygroundImageInput }
  | { modality: "video"; input: PlaygroundVideoInput };

export interface PendingSubmit {
  clientRequestId: string;
  fingerprint: string;
}

export interface PlaygroundMedia {
  type: "image" | "video";
  url: string;
}

export interface GenerationStatusView {
  status: string;
  terminal: boolean;
  succeeded: boolean;
  tone: "info" | "success" | "error" | "warning";
  message: string;
}

// playgroundModels keeps only fixture models that can run async in this modality, preserving backend order.
// 只保留该模态下支持异步提交且有可用路由的模型，顺序与后端一致。
export function playgroundModels(capabilities: PlaygroundCapability[], modality: Modality): PlaygroundModel[] {
  const supportedModes = modality === "image" ? IMAGE_MODES : VIDEO_MODES;
  return capabilities.flatMap((capability) => {
    if (capability.modality !== modality || !list(capability.response_modes).includes("async")) return [];
    const rawRoutes: Array<PlaygroundImageRoute | PlaygroundVideoRoute> = modality === "image" ? list(capability.image_routes) : list(capability.video_routes);
    const routes = rawRoutes.map(normalizeRoute).filter((route) => supportedModes.includes(route.mode));
    return routes.length ? [{ key: capability.model, modality, routes }] : [];
  });
}

// modeCandidates is the D5 selection table ordered by preference.
// D5 模式选择表，按优先级排列。
export function modeCandidates(modality: Modality, imageCount: number): string[] {
  if (modality === "image") return imageCount === 0 ? ["text_to_image"] : ["image_to_image"];
  if (imageCount === 0) return ["text_to_video"];
  if (imageCount === 1) return ["image_to_video", "reference_to_video"];
  if (imageCount === 2) return ["reference_to_video", "image_to_video"];
  return ["reference_to_video"];
}

// uploadLimit is the largest reference_images.max across the model's image-input routes, capped at 9.
// 上传上限 = 模型所有带图路由 reference_images.max 的最大值，且不超过 9。
export function uploadLimit(model: PlaygroundModel | null): number {
  if (!model) return 0;
  const relevant = model.modality === "image" ? ["image_to_image"] : ["image_to_video", "reference_to_video"];
  const limit = model.routes.filter((route) => relevant.includes(route.mode)).reduce((current, route) => Math.max(current, routeMaxImages(route)), 0);
  return Math.min(limit, PLAYGROUND_UPLOAD_HARD_LIMIT);
}

// resolveMode picks the first preferred mode whose route exists and accepts the image count.
// 选择第一个存在且图片数量在 reference_images 范围内的优先模式。
export function resolveMode(model: PlaygroundModel, imageCount: number): ModeResolution {
  const limit = uploadLimit(model);
  if (imageCount > limit) {
    return blocked(limit === 0 ? "当前模型不支持参考图，请删除已上传的图片" : `当前模型最多支持 ${limit} 张参考图，请删除多余图片`);
  }
  const candidates = modeCandidates(model.modality, imageCount);
  const existing = candidates.filter((mode) => model.routes.some((route) => route.mode === mode));
  for (const mode of existing) {
    const routes = model.routes.filter((route) => route.mode === mode && routeAcceptsCount(route, imageCount));
    if (routes.length) return { allowed: true, mode, routes, hint: modeLabel(mode, imageCount) };
  }
  if (!existing.length) return blocked(missingModeHint(model.modality, imageCount));
  const minimum = Math.min(...model.routes.filter((route) => existing.includes(route.mode)).map((route) => route.minImages));
  if (imageCount < minimum) return blocked(`当前模型至少需要 ${minimum} 张参考图`);
  return blocked(`当前模型不支持 ${imageCount} 张参考图，请调整图片数量`);
}

// resolvePlaygroundForm clamps a draft to legal values after any model, mode or image-count change.
// 模型、模式或图片数量变化后，把草稿收敛到合法取值。
export function resolvePlaygroundForm(models: PlaygroundModel[], modality: Modality, draft: PlaygroundSelection, imageCount: number): PlaygroundForm {
  const model = models.find((item) => item.key === draft.model) || models[0] || null;
  const limit = uploadLimit(model);
  if (!model) {
    const mode = blocked(`测试 Consumer 当前没有可用的${modality === "image" ? "图片" : "视频"}模型`);
    return { modality, model, mode, uploadLimit: limit, aspectRatios: [], resolutions: [], durations: [], selection: { model: "", aspectRatio: "", resolution: "", durationSeconds: 0 }, blockedReason: mode.hint };
  }
  const mode = resolveMode(model, imageCount);
  const routes = mode.allowed ? mode.routes : [];
  const aspectRatios = unionStrings(routes.map((route) => route.aspectRatios));
  const aspectRatio = pickString(aspectRatios, draft.aspectRatio, PREFERRED_ASPECT_RATIOS);
  const aspectRoutes = routes.filter((route) => acceptsString(route.aspectRatios, aspectRatio));
  const resolutions = unionStrings(aspectRoutes.map((route) => route.resolutions));
  const resolution = pickString(resolutions, draft.resolution, []);
  const resolutionRoutes = aspectRoutes.filter((route) => acceptsString(route.resolutions, resolution));
  const durations = modality === "video" ? unionNumbers(resolutionRoutes.map((route) => route.durations)).sort((left, right) => left - right) : [];
  const durationSeconds = modality === "video" ? (durations.includes(draft.durationSeconds) ? draft.durationSeconds : durations[durations.length - 1] ?? 0) : 0;
  let blockedReason = "";
  if (!mode.allowed) blockedReason = mode.hint;
  else if (!resolutions.length) blockedReason = "当前模型没有声明可用分辨率，暂时无法提交";
  else if (modality === "video" && !durations.length) blockedReason = "当前模型没有声明可用时长，暂时无法提交";
  return {
    modality, model, mode, uploadLimit: limit, aspectRatios, resolutions, durations,
    selection: { model: model.key, aspectRatio, resolution, durationSeconds },
    blockedReason,
  };
}

// sameSelection compares two selections field by field.
// 逐字段比较两个选择是否一致。
export function sameSelection(left: PlaygroundSelection, right: PlaygroundSelection): boolean {
  return left.model === right.model && left.aspectRatio === right.aspectRatio && left.resolution === right.resolution && left.durationSeconds === right.durationSeconds;
}

// buildPlaygroundPayload maps a submittable form onto the narrow admin input; returns null when blocked.
// 把可提交的表单映射为窄输入；不可提交时返回 null。
export function buildPlaygroundPayload(form: PlaygroundForm, prompt: string, referenceUrls: string[], clientRequestId: string, routing?: Pick<PlaygroundImageInput, "test_mode" | "channel_id">): PlaygroundPayload | null {
  const trimmedPrompt = prompt.trim();
  if (form.blockedReason || !form.model || !form.mode.mode || !trimmedPrompt) return null;
  const { model, aspectRatio, resolution, durationSeconds } = form.selection;
  if (form.modality === "image") {
    return { modality: "image", input: { ...routing, client_request_id: clientRequestId, model, prompt: trimmedPrompt, aspect_ratio: aspectRatio, resolution, reference_images: [...referenceUrls] } };
  }
  const imageToVideo = form.mode.mode === "image_to_video";
  return {
    modality: "video",
    input: {
      ...routing, client_request_id: clientRequestId, model, prompt: trimmedPrompt, generation_mode: form.mode.mode,
      aspect_ratio: aspectRatio, resolution, duration_seconds: durationSeconds,
      first_frame_image: imageToVideo ? referenceUrls[0] || "" : "",
      last_frame_image: imageToVideo ? referenceUrls[1] || "" : "",
      reference_images: form.mode.mode === "reference_to_video" ? [...referenceUrls] : [],
    },
  };
}

// withClientRequestId returns a copy of the payload carrying the chosen idempotency id.
// 返回带指定幂等 ID 的提交副本。
export function withClientRequestId(payload: PlaygroundPayload, clientRequestId: string): PlaygroundPayload {
  return payload.modality === "image"
    ? { modality: "image", input: { ...payload.input, client_request_id: clientRequestId } }
    : { modality: "video", input: { ...payload.input, client_request_id: clientRequestId } };
}

// payloadFingerprint identifies a submission independent of its client_request_id.
// 生成与 client_request_id 无关的提交指纹。
export function payloadFingerprint(payload: PlaygroundPayload): string {
  return JSON.stringify({ modality: payload.modality, ...payload.input, client_request_id: "" });
}

// clientRequestIdFor reuses the pending id only when retrying the identical payload.
// 只有原样重试同一份提交时才复用待确认的请求 ID，否则生成新 ID。
export function clientRequestIdFor(pending: PendingSubmit | null, fingerprint: string, generate: () => string): string {
  return pending && pending.fingerprint === fingerprint ? pending.clientRequestId : generate();
}

// isAmbiguousSubmitFailure reports failures where the server may already have accepted the request.
// 判断提交失败时服务端是否可能已经受理；这类失败重试必须复用同一个 ID。
export function isAmbiguousSubmitFailure(error: unknown): boolean {
  if (!(error instanceof ApiError)) return true;
  if (error.code === PLAYGROUND_NOT_CONFIGURED_CODE) return false;
  return error.status === 0 || error.status === 408 || error.status === 429 || error.status >= 500;
}

// isPlaygroundNotConfigured detects the 503 not-configured contract.
// 识别 503 未配置测试 Consumer 的约定错误。
export function isPlaygroundNotConfigured(error: unknown): boolean {
  return error instanceof ApiError && error.status === 503 && error.code === PLAYGROUND_NOT_CONFIGURED_CODE;
}

// parseGenerationStatus projects the public status; unknown values keep polling so paid work is never resubmitted.
// 解析公开状态；未知状态按进行中处理并继续轮询，避免重复付费提交。
export function parseGenerationStatus(status: string | undefined, errorMessage = ""): GenerationStatusView {
  const value = String(status || "").toLowerCase();
  switch (value) {
    case "queued": return { status: value, terminal: false, succeeded: false, tone: "info", message: "已提交，正在排队" };
    case "succeeded": return { status: value, terminal: true, succeeded: true, tone: "success", message: "生成完成" };
    case "failed": return { status: value, terminal: true, succeeded: false, tone: "error", message: errorMessage.trim() || "生成失败" };
    case "uncertain": return { status: value, terminal: true, succeeded: false, tone: "warning", message: "结果不确定，请勿重复提交" };
    default: return { status: value || "running", terminal: false, succeeded: false, tone: "info", message: "生成中" };
  }
}

export function isTerminalStatus(status: string | undefined): boolean {
  return parseGenerationStatus(status).terminal;
}

// generationMedia reads the archived result manifest (images[] or video_url).
// 从结果 manifest 中读取图片列表或视频地址。
export function generationMedia(result: Record<string, unknown> | null | undefined): PlaygroundMedia[] {
  if (!result) return [];
  const images = Array.isArray(result.images) ? result.images : [];
  const media: PlaygroundMedia[] = images.filter(isSafeMediaURL).map((url) => ({ type: "image", url }));
  if (isSafeMediaURL(result.video_url)) media.push({ type: "video", url: result.video_url });
  return media;
}

export function historyMedia(item: PlaygroundHistoryItem): PlaygroundMedia[] {
  const type = item.modality === "video" ? "video" : "image";
  return list(item.result_urls).filter(isSafeMediaURL).map((url) => ({ type, url }));
}

// validateUploadFile mirrors the backend limits for early feedback; the server stays authoritative.
// 前端提前校验类型和大小以便提示，最终以后端校验为准。
export function validateUploadFile(file: { name: string; type: string; size: number }): string {
  if (!PLAYGROUND_UPLOAD_TYPES.includes(file.type)) return `${file.name}：只支持 PNG、JPEG、WebP 图片`;
  if (file.size <= 0) return `${file.name}：文件为空`;
  if (file.size > PLAYGROUND_UPLOAD_MAX_BYTES) return `${file.name}：单张图片不能超过 10MB`;
  return "";
}

export function aspectRatioLabel(value: string): string {
  const normalized = value.trim();
  if (normalized === "16:9") return "横屏 16:9";
  if (normalized === "9:16") return "竖屏 9:16";
  if (normalized.toLowerCase() === "auto") return "自动";
  return normalized;
}

export function resolutionLabel(value: string): string {
  const normalized = value.trim().toLowerCase();
  if (normalized === "1k") return "标准";
  if (/^\d+k$/.test(normalized)) return normalized.toUpperCase();
  return value.trim();
}

export function durationLabel(seconds: number): string {
  return `${seconds} 秒`;
}

// elapsedSeconds measures created → finished, or created → now while still running.
// 计算耗时：已结束用完成时间，进行中用当前时间。
export function elapsedSeconds(createdAt: number, finishedAt: number | undefined, nowSeconds: number): number | null {
  if (!createdAt) return null;
  return Math.max(0, (finishedAt || nowSeconds) - createdAt);
}

export function formatElapsed(seconds: number | null): string {
  if (seconds === null) return "-";
  if (seconds < 60) return `${seconds} 秒`;
  return `${Math.floor(seconds / 60)} 分 ${seconds % 60} 秒`;
}

// newClientRequestId prefers crypto.randomUUID and falls back to a v4 UUID from getRandomValues outside secure contexts.
// 优先使用 crypto.randomUUID；非安全上下文下用 getRandomValues 生成 v4 UUID。
export function newClientRequestId(): string {
  const source = globalThis.crypto;
  if (typeof source?.randomUUID === "function") return source.randomUUID();
  const bytes = new Uint8Array(16);
  source.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function normalizeRoute(route: PlaygroundImageRoute | PlaygroundVideoRoute): PlaygroundRoute {
  const range = route.reference_images || {};
  return {
    mode: String(route.generation_mode || "").trim().toLowerCase(),
    aspectRatios: unionStrings([list(route.aspect_ratios)]),
    resolutions: unionStrings([list(route.resolutions)]),
    durations: "duration_seconds" in route ? unionNumbers([list(route.duration_seconds)]).filter((value) => Number.isInteger(value) && value > 0) : [],
    minImages: typeof range.min === "number" && range.min > 0 ? Math.floor(range.min) : 0,
    maxImages: typeof range.max === "number" && range.max >= 0 ? Math.floor(range.max) : null,
  };
}

function routeMaxImages(route: PlaygroundRoute): number {
  if (route.mode === "text_to_image" || route.mode === "text_to_video") return 0;
  const declared = route.maxImages ?? PLAYGROUND_UPLOAD_HARD_LIMIT;
  const capped = route.mode === "image_to_video" ? Math.min(declared, IMAGE_TO_VIDEO_MAX_FRAMES) : declared;
  return Math.min(capped, PLAYGROUND_UPLOAD_HARD_LIMIT);
}

function routeAcceptsCount(route: PlaygroundRoute, imageCount: number): boolean {
  return imageCount >= route.minImages && imageCount <= routeMaxImages(route);
}

function modeLabel(mode: string, imageCount: number): string {
  switch (mode) {
    case "text_to_image": return "文生图";
    case "image_to_image": return "图生图";
    case "text_to_video": return "文生视频";
    case "image_to_video": return imageCount >= 2 ? "图生视频（首尾帧）" : "图生视频（首帧）";
    case "reference_to_video": return "参考图生视频";
    default: return mode;
  }
}

function missingModeHint(modality: Modality, imageCount: number): string {
  if (modality === "image") return imageCount === 0 ? "当前模型不支持文生图，请上传参考图" : "当前模型不支持图生图，请删除参考图";
  if (imageCount === 0) return "当前模型不支持文生视频，请上传参考图";
  return `当前模型不支持用 ${imageCount} 张图片生成视频，请调整图片数量`;
}

function blocked(hint: string): ModeResolution {
  return { allowed: false, mode: null, routes: [], hint };
}

function acceptsString(values: string[], current: string): boolean {
  return values.length === 0 || values.some((value) => value.toLowerCase() === current.toLowerCase());
}

function pickString(options: string[], current: string, preferred: string[]): string {
  const match = (value: string) => options.find((option) => option.toLowerCase() === value.trim().toLowerCase());
  return match(current) || preferred.map(match).find(Boolean) || options[0] || "";
}

function unionStrings(groups: string[][]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  groups.flat().forEach((raw) => {
    const value = String(raw).trim();
    if (value && !seen.has(value.toLowerCase())) {
      seen.add(value.toLowerCase());
      result.push(value);
    }
  });
  return result;
}

function unionNumbers(groups: number[][]): number[] {
  return Array.from(new Set(groups.flat().map(Number).filter(Number.isFinite)));
}

function isSafeMediaURL(value: unknown): value is string {
  if (typeof value !== "string") return false;
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

function list<T>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : [];
}

export function playgroundRoutingLabel(routing: PlaygroundRouting | undefined): string {
  if (!routing) return "测试模式未知";
  if (routing.test_mode === "channel") return `指定渠道测试 · ${routing.channel_name || `渠道 #${routing.channel_id}`}`;
  return routing.test_mode === "model" ? "模型测试 · 禁止跨模型降级" : "业务策略测试 · 允许替换与降级";
}
